import { createMemo, type Accessor } from 'solid-js'
import type { Poster } from './posterSchema'
import type { RuntimeObject, RuntimeContainer, RuntimeImage, Dimensions, Box } from '../types'
import type { ImplRegistry } from '../canvas/interactions/ObjectImpl'
import { assignIds, migrateGeometry } from '../canvas/utils'
import { layoutDesign, type LeafMetrics, type MetricsOf } from './designLayout'

// ── Linked-design state ─────────────────────────────────────────────────────────
//
// In user mode the visible poster is a *flat* list of leaves (posterNEW.design). This state keeps
// that flat view linked to the original nested *template* design, which stays the layout authority:
// its containers lay the leaves out (via designLayout), so the flat leaves keep reflowing when the
// record changes. Each flat leaf maps to a template leaf by shared id.
//
// The engine reads each leaf's *size* straight from the mounted flat leaves (leafImpls, i.e. the
// selection registry) — the "reactive flattened dims". A flat field that resolves empty is simply not
// mounted, so it drops out of the layout and reappears (measured) when the record refills it; no
// hidden measurement mount is needed. (If the flat view is ever changed to bake static text, a hidden
// text-measure layer would have to feed sizes here instead.)
//
// The template tree is derived (a clone of the fetched design) rather than a mutable store: a
// `detached` overlay swaps a dragged-out leaf for a fixed-size dummy container, so it drops from the
// layout's reactive input and holds its slot at a frozen snapshot size — the other leaves stay put.
//
// That overlay is read from the flat leaves themselves (each carries its own `detached` slot size, set
// when it was dragged off), not from a signal here. It therefore travels with the design: a leaf
// dragged in one session reloads still detached, and so keeps the position the drag gave it rather than
// being laid out — and then re-baked — back onto the template. It also needs no reset when a fresh flat
// view is built, since the detachments are part of the view being replaced.
//
// Read-only / design-mode hosts pass no linkedDesign; the state is then inert (linked() === false).

export interface DesignLinkContext {
	linked: Accessor<boolean>
	// The template tree with detached leaves replaced by dummy shapes — the layout engine's input.
	objects: Accessor<RuntimeObject[]>
	// The ids the write-back should drive: every laid-out object that isn't detached. Read from the
	// design side so the write-back never has to track the flat store it writes into.
	linkedIds: Accessor<string[]>
	// The leaf's laid-out absolute box, or undefined when it isn't a linked leaf (or resolved empty).
	// It is the box the layout *placed*, not necessarily the one the leaf draws: a hugging field is
	// placed at its design width, so the host runs it through impl.displayBox() first.
	laidOut: (id: string) => Box | undefined
	// Whether this id is a linked leaf the write-back should drive (present in the layout, not detached).
	isLinkedLeaf: (id: string) => boolean
	isDetached: (id: string) => boolean
}

// flatLeaves is the flat design being laid out — the same objects the write-back writes into. It is
// read for one thing only: which leaves carry a `detached` slot (setDetachedInStore is the writer).
export function createDesignLinkState(linkedDesign: (() => Poster | undefined) | undefined, flatLeaves: () => RuntimeObject[], leafImpls: ImplRegistry, canvas: () => Dimensions): DesignLinkContext
{
	// Reads only each leaf's id and detached slot, so the position writes the bake makes don't
	// re-run it (a Solid store tracks per property) and can't feed the layout back into itself.
	const detached = createMemo<Map<string, Dimensions>>(() => {
		const map = new Map<string, Dimensions>()
		collectDetached(flatLeaves(), map)
		return map
	})

	// A fresh clone of the fetched template, re-seeded only when the resource identity changes (a
	// reset / theme swap). assignIds/migrateGeometry mirror createObjectState's read-only seed so
	// leaf ids line up with the flat store. Empty when there is no linked design.
	const base = createMemo<RuntimeObject[]>(() => {
		const design = linkedDesign?.()
		if (!design?.objects?.length) return []
		const clone = JSON.parse(JSON.stringify(design.objects)) as RuntimeObject[]
		migrateGeometry(clone)
		assignIds(clone)
		return clone
	})

	// The tree fed to the layout engine: detached leaves swapped for fixed-size dummy containers.
	const objects = createMemo<RuntimeObject[]>(() => {
		const d = detached()
		return d.size ? applyDummies(base(), d) : base()
	})

	// Leaf metrics come from the mounted flat leaf (size is position-free, so reading it forms no
	// layout cycle); an un-mounted image falls back to its stored viewport size. Dummies are
	// containers and never reach this — the engine derives their size from their stored box.
	// layoutSize, not getSize: a field is laid out in its design width and only draws hugged (see
	// PlainWrap.layoutSize). Everything else lays out at the size it draws.
	const metricsOf: MetricsOf = (id: string): LeafMetrics | undefined => {
		const impl = leafImpls[id]
		if (impl) return { size: impl.layoutSize(), coy: impl.getContentOffsetY() }
		const node = nodeById(objects(), id)
		if (node?.type === 'image') {
			const s = (node as RuntimeImage).size
			if (s?.width != null && s?.height != null) return { size: { width: s.width, height: s.height }, coy: 0 }
		}
		return undefined
	}

	const laidOutMap = createMemo<Map<string, Box>>(() => {
		if (!base().length) return new Map()
		return layoutDesign(objects(), metricsOf, canvas())
	})

	return {
		linked:       () => (base().length > 0),
		objects,
		linkedIds:    () => { const d = detached(); return [...laidOutMap().keys()].filter(id => !d.has(id)) },
		laidOut:      (id) => detached().has(id) ? undefined : laidOutMap().get(id),
		isLinkedLeaf: (id) => !detached().has(id) && laidOutMap().has(id),
		isDetached:   (id) => detached().has(id),
	}
}

function collectDetached(objects: RuntimeObject[], map: Map<string, Dimensions>): void
{
	for (const o of objects) {
		if (o.detached) map.set(o.id, o.detached)
		if (o.type === 'container' && o.objects?.length) collectDetached(o.objects, map)
	}
}

// A childless absolute container carrying the vacated slot's size — the fixed dummy that holds a
// detached leaf's slot in the template so its siblings' layout is undisturbed (see designLayout: a
// childless container measures to its stored size). Local position is kept so an absolute parent
// still slots it.
function toDummy(node: RuntimeObject, slot: Dimensions): RuntimeContainer
{
	return {
		id:       node.id,
		type:     'container',
		position: node.position,
		size:     { width: slot.width, height: slot.height },
		layout:   'absolute',
	}
}

function applyDummies(objects: RuntimeObject[], detached: Map<string, Dimensions>): RuntimeObject[]
{
	return objects.map(o => {
		const snap = detached.get(o.id)
		if (snap) return toDummy(o, snap)
		if (o.type === 'container' && o.objects?.length)
			return { ...o, objects: applyDummies(o.objects, detached) }
		return o
	})
}

function nodeById(objects: RuntimeObject[], id: string): RuntimeObject | null
{
	for (const o of objects) {
		if (o.id === id) return o
		if (o.type === 'container' && o.objects?.length) {
			const hit = nodeById(o.objects, id)
			if (hit) return hit
		}
	}
	return null
}
